import type { Identifier } from "ra-core";

import type {
  ContactAudienceSelection,
  ContactCohortFilter,
  WhatsAppBroadcast,
} from "../types";

/**
 * Editing a saved broadcast, CRM side.
 *
 * Operators save drafts by scheduling them, so "edit my draft" is "rewrite an
 * unsent scheduled broadcast". The API rebuilds the broadcast from the same
 * create pipeline, so all this module does is (a) decide whether the Edit
 * affordance is offered at all and (b) project a stored broadcast back onto the
 * dialog's form state.
 *
 * The editability rule is deliberately a mirror of the API's own gate
 * (`BROADCAST_EDITABLE_STATUSES` + `BROADCAST_DISPATCHED_COUNT_KEYS` in
 * dashboard-api). The API stays authoritative — it re-checks the materialized
 * recipient rows, which the CRM cannot see — so this is purely about not
 * offering a button that would 409.
 */

/** Parked states an operator can still revise. Everything else is mid-flight. */
export const EDITABLE_BROADCAST_STATUSES = ["scheduled", "paused"] as const;

/** Any non-zero count here means the provider already saw this broadcast. */
export const DISPATCHED_COUNT_FIELDS = [
  "sending_count",
  "accepted_count",
  "held_count",
  "sent_count",
  "delivered_count",
  "read_count",
  "failed_count",
  "cancelled_count",
] as const satisfies readonly (keyof WhatsAppBroadcast)[];

export function broadcastDispatchStarted(
  broadcast: Pick<WhatsAppBroadcast, (typeof DISPATCHED_COUNT_FIELDS)[number]>,
): boolean {
  return DISPATCHED_COUNT_FIELDS.some(
    (field) => Number(broadcast[field] ?? 0) > 0,
  );
}

export function canEditBroadcast(
  broadcast: WhatsAppBroadcast | null | undefined,
): boolean {
  if (!broadcast) return false;
  if (
    !(EDITABLE_BROADCAST_STATUSES as readonly string[]).includes(
      broadcast.status,
    )
  ) {
    return false;
  }
  return !broadcastDispatchStarted(broadcast);
}

export type BroadcastEditDraft = {
  name: string;
  senderKey: string;
  templateId: string;
  parameters: Record<string, string>;
  audienceSelection: ContactAudienceSelection | null;
  scheduledForInput: string;
  includeNotOptedIn: boolean;
  complianceAttested: boolean;
  headerMediaAssetId: string | null;
};

/**
 * Re-express a stored audience as the composer's two audience *inputs*.
 *
 * The composer does not read a selection directly — it seeds the contact
 * selector from `contactIds` and the cohort path from `contactFilter`, then
 * lets those rebuild the selection. Feeding an edit through the same two inputs
 * means the hydrated audience travels the identical, already-proven path rather
 * than a second one that has to stay in sync with it.
 */
export function broadcastAudienceInputs(
  broadcast: WhatsAppBroadcast | null | undefined,
): {
  contactIds?: Identifier[];
  contactFilter?: ContactCohortFilter;
} {
  const audience = broadcast?.audience_selection;
  if (!audience) return {};
  if (audience.mode === "filter") {
    return { contactFilter: (audience.filter ?? {}) as ContactCohortFilter };
  }
  return { contactIds: audience.contact_ids ?? [] };
}

/**
 * `datetime-local` reads and writes wall-clock time in the viewer's zone with
 * no zone suffix, so an ISO instant has to be shifted by the local offset
 * before it can be sliced into the `YYYY-MM-DDTHH:mm` the input expects.
 * Anything unparseable becomes "" — an empty picker is recoverable, a wrong
 * send time is not.
 */
export function toDateTimeLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const local = new Date(
    parsed.getTime() - parsed.getTimezoneOffset() * 60_000,
  );
  return local.toISOString().slice(0, 16);
}

/**
 * Project a stored broadcast onto the dialog's form state.
 *
 * Only an operator's own header-media override is carried back: a broadcast
 * that inherited the template's image has `header_media_source: "template"`,
 * and re-sending that id as an override would pin today's asset onto a
 * template whose media may since have changed.
 */
export function broadcastEditDraft(
  broadcast: WhatsAppBroadcast,
): BroadcastEditDraft {
  return {
    name: broadcast.name ?? "",
    senderKey: broadcast.sender_key ?? "",
    templateId: broadcast.template_id ? String(broadcast.template_id) : "",
    parameters: { ...(broadcast.default_template_parameters ?? {}) },
    audienceSelection: broadcast.audience_selection ?? null,
    scheduledForInput: toDateTimeLocalInput(broadcast.scheduled_for),
    includeNotOptedIn: Boolean(broadcast.include_not_opted_in),
    complianceAttested: Boolean(broadcast.operator_compliance_attestation),
    headerMediaAssetId:
      broadcast.header_media_source === "override" &&
      broadcast.header_media_asset_id
        ? String(broadcast.header_media_asset_id)
        : null,
  };
}
